import { faCircleNotch, faEllipsisV } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Menu, Transition } from '@headlessui/react'
import { DMMF } from '@prisma/client/runtime'
import { AxiosResponse } from 'axios'
import classNames from 'classnames'
import React, { Fragment, useState } from 'react'
import { useForm } from 'react-hook-form'
import { UseMutationResult } from 'react-query'
import { DiaperBankPublic, DiaperBankUpdateDTO } from '../../utils/DiaperBankDTO'
import Button from '../form/Button'
import FormFieldInput from '../form/FormFieldInput'
import FormSwitch from '../form/FormSwitch'
import Modal from '../Modal'
import { toastMessage } from '../toastMessage'

interface EditUpdateArgs {
  text: string
  isPublic: boolean
}

export default function UpdateManageDropdown({
  update,
  deleteMutation,
  updateMutation,
}: {
  update: DiaperBankUpdateDTO
  updateMutation: UseMutationResult<
    AxiosResponse<DiaperBankPublic>,
    unknown,
    {
      text?: string | undefined
      isPublic?: boolean | undefined
      id: number
    },
    unknown
  >
  deleteMutation: UseMutationResult<AxiosResponse<DiaperBankPublic>, unknown, { id: number }, unknown>
}) {
  const deleteUpdate = async () => {
    if (confirm('Are you sure?')) {
      await deleteMutation.mutateAsync({ id: update.id })
      toastMessage({ title: 'Update has been deleted', type: 'success' })
      setIsOpen(false)
    }
  }

  const { register, handleSubmit, formState, control, setValue, watch, reset } = useForm<EditUpdateArgs>({
    defaultValues: {
      text: update.text,
      isPublic: update.isPublic,
    },
  })

  const [editing, setEditing] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const onSubmit = async ({ text, isPublic }: EditUpdateArgs) => {
    try {
      await updateMutation.mutateAsync({ text, isPublic, id: update.id })
      setEditing(false)
      setIsOpen(false)
      toastMessage({ title: 'Update has been saved', type: 'success' })
    } catch (error) {
      console.error(error)
      toastMessage({ title: 'An error occurred when saving this update', type: 'error' })
    }
  }

  return (
    <div className='absolute top-0 right-0 mr-2 mt-2'>
      {editing && (
        <Modal open={editing} onClose={() => setEditing(false)} title='Edit'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormFieldInput label='Message' register={register('text', { required: true })} required errors={formState.errors} autoFocus type='textarea' rows={8} />
            <FormSwitch label='Share the post on your public timeline?' onChange={(val) => setValue('isPublic', val, { shouldDirty: true })} defaultValue={update.isPublic} />
            <Button type='submit' fullWidth className='mt-4' loading={updateMutation.isLoading} color='primary'>
              Save
            </Button>
            <div className='my-4 text-xs'>Subscribed contributors will NOT get another email about this update</div>
          </form>
        </Modal>
      )}

      <Menu as='div' className='relative inline-block text-left float-right'>
        <div>
          <Menu.Button className='dropdown-btn-with-icon dropdown-btn-transparent'>
            <FontAwesomeIcon icon={faEllipsisV} />
            {/* <ChevronDownIcon className='-mr-1 ml-2 h-5 w-5' aria-hidden='true' /> */}
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter='transition ease-out duration-100'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <Menu.Items className='dropdown-items'>
            <div className='py-1'>
              <Menu.Item>
                {({ active }) => (
                  <button onClick={() => setEditing(true)} disabled={updateMutation.isLoading} className={classNames('dropdown-item', { active })}>
                    <FontAwesomeIcon icon={faCircleNotch} className={classNames('mr-1', { hidden: !updateMutation.isLoading })} spin />
                    Edit
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button disabled={deleteMutation.isLoading} onClick={() => deleteUpdate()} className={classNames('dropdown-item', { active })}>
                    <FontAwesomeIcon icon={faCircleNotch} className={classNames('mr-1', { hidden: !deleteMutation.isLoading })} spin />
                    Delete
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  )
}
